import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { paOverview } from "@/lib/api";
import { Kpi, money, moneyM, pct, mult, num, OutcomeBadge, H } from "./paShared";

function RegionBlock({ region }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { paOverview(region).then(setD).catch((e) => setErr(e?.response?.data?.detail || e.message)); }, [region]);
  if (err) return <p className="text-sm text-rose-600">{err}</p>;
  if (!d) return <div className="flex items-center gap-2 text-sm text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading {region}…</div>;
  if (!d.all.loans) return <p className="text-sm text-slate-500 py-4">No {region} data yet — run a sync from the Sync status tab.</p>;
  const m = d.matured, a = d.all, lb = d.live_book;
  return (
    <div className="space-y-4" data-testid={`pa-overview-${region}`}>
      <div className="flex items-baseline justify-between">
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{region === "AUS" ? "Australia" : "United Kingdom"}</h3>
        <p className="text-[11px] text-slate-500">As of {d.as_of || "—"}{d.synced_at ? ` · synced ${new Date(d.synced_at).toLocaleString("en-GB")}` : ""}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi label="Loans funded" value={num(a.loans)} sub={`${num(a.fully_paid)} fully paid`} testid={`pa-kpi-${region}-loans`} help="loans_funded" />
        <Kpi label="Principal lent" value={moneyM(a.principal, region)} sub={`avg ${money(a.avg_loan, region)}`} testid={`pa-kpi-${region}-principal`} help="principal_lent" />
        <Kpi label="Matured book" value={num(m.loans)} sub={`${moneyM(m.principal, region)} principal`} testid={`pa-kpi-${region}-matured`} help="matured_book" />
        <Kpi label="Matured bad rate" value={pct(m.bad_pct)} sub={`legal / W-O ${pct(m.legal_wo_pct)}`} tone={m.bad_pct >= 15 ? "bad" : "default"} testid={`pa-kpi-${region}-bad`} help="matured_bad" />
        <Kpi label="Matured cash multiple" value={mult(m.cash_multiple)} sub={`day-91 ${mult(m.cash_by_day91_multiple)}`} tone={m.cash_multiple >= 1.3 ? "good" : "default"} testid={`pa-kpi-${region}-multiple`} help="matured_multiple" />
        <Kpi label="Settled by renewal" value={pct(m.settled_by_renewal_pct)} sub={`${num(m.settled_by_renewal)} of ${num(m.fully_paid)} matured paid`} testid={`pa-kpi-${region}-renewal`} help="settled_renewal" />
      </div>
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
          <p className="text-xs font-semibold text-slate-700"><H k="live_book">Live book by outcome</H></p>
          <p className="text-[11px] text-slate-500">{num(lb.loans)} loans · {moneyM(lb.principal, region)} principal · {moneyM(lb.outstanding_balance, region)} outstanding</p>
        </div>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase text-slate-500"><tr><th className="px-3 py-1.5 text-left"><H k="outcome">Outcome</H></th><th className="px-3 py-1.5 text-right"><H k="loans">Loans</H></th><th className="px-3 py-1.5 text-right"><H k="principal">Principal</H></th><th className="px-3 py-1.5 text-right"><H k="outstanding_balance">Outstanding</H></th><th className="px-3 py-1.5 text-right"><H k="cash_multiple">Cash multiple</H></th><th className="px-3 py-1.5 text-right"><H k="never_paid">Never paid</H></th><th className="px-3 py-1.5 text-right"><H k="not_yet_due">Not yet due</H></th></tr></thead>
          <tbody>
            {lb.by_outcome.map((r) => (
              <tr key={r.group} className="border-t border-slate-100" data-testid={`pa-live-${region}-${r.group.replace(/[^a-z]/gi, "-")}`}>
                <td className="px-3 py-1.5"><OutcomeBadge outcome={r.group} /></td>
                <td className="px-3 py-1.5 text-right tabular-nums">{num(r.loans)} <span className="text-slate-400">({pct(r.loans / lb.loans * 100)})</span></td>
                <td className="px-3 py-1.5 text-right tabular-nums">{money(r.principal, region)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{money(r.outstanding_balance, region)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{mult(r.cash_multiple)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{num(r.never_paid)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{num(r.not_yet_due)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PaOverview() {
  return (
    <div className="space-y-8">
      <RegionBlock region="UK" />
      <RegionBlock region="AUS" />
    </div>
  );
}
